import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { getIdentity, refreshIdentity, type BffIdentity } from "@/shared/api/identity";
import {
  getSessionSnapshot,
  refreshSession,
  subscribeSession,
  type SessionStatus,
} from "@/shared/api/session";

interface SessionUser {
  id: string;
  name: string;
  email: string;
}

interface SessionData {
  user: SessionUser;
}

export interface UseSessionResult {
  status: SessionStatus;
  data: SessionData | null;
  update: (patch?: { retryBffAuth?: boolean }) => Promise<SessionData | null>;
}

export function useSession(): UseSessionResult {
  const snapshot = useSyncExternalStore(subscribeSession, getSessionSnapshot);
  return { status: snapshot.status, data: toData(snapshot), update };
}

export interface UseIdentityResult {
  identity: BffIdentity | null;
  loading: boolean;
  error: unknown;
  retry: () => void;
}

export function useIdentity(): UseIdentityResult {
  const { status, data } = useSession();
  const userId = data?.user.id ?? null;
  const [state, setState] = useState<UseIdentityResult>({
    identity: null,
    loading: true,
    error: null,
    retry: () => undefined,
  });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt is an explicit identity reload trigger.
  useEffect(() => {
    if (status !== "authenticated" || !userId) {
      setState({ identity: null, loading: false, error: null, retry });
      return;
    }
    let live = true;
    setState({ identity: null, loading: true, error: null, retry });
    getIdentity().then(
      (identity) => live && setState({ identity, loading: false, error: null, retry }),
      (error: unknown) => live && setState({ identity: null, loading: false, error, retry }),
    );
    return () => {
      live = false;
    };
  }, [status, userId, attempt, retry]);

  return state;
}

function toData(snapshot: ReturnType<typeof getSessionSnapshot>): SessionData | null {
  const s = snapshot.session;
  if (!s || snapshot.status !== "authenticated") return null;
  return { user: { id: s.userId, name: s.userName, email: s.userName } };
}

async function update(patch?: { retryBffAuth?: boolean }): Promise<SessionData | null> {
  if (patch?.retryBffAuth) await refreshSession();
  else await refreshIdentity().catch(() => null);
  return toData(getSessionSnapshot());
}
